import { useState } from 'react';
import { Settings as SettingsIcon, User, Shield, Bell, Palette, Database, Users, Lock, HelpCircle, Link as LinkIcon, TestTube } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AccountSettings from '../components/Settings/AccountSettings';
import SecuritySettings from '../components/Settings/SecuritySettings';
import SessionsSettings from '../components/Settings/SessionsSettings';
import NotificationSettings from '../components/Settings/NotificationSettings';
import TeamSettings from '../components/Settings/TeamSettings';
import RolesPermissionsSettings from '../components/Settings/RolesPermissionsSettings';
import DataSettings from '../components/Settings/DataSettings';
import SecurityPrivacySettings from '../components/Settings/SecurityPrivacySettings';
import ApiConnectionsSettings from '../components/Settings/ApiConnectionsSettings';

export default function Settings() {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState('account');
  const [activeSubMenu, setActiveSubMenu] = useState('general');

  const menuItems = [
    {
      id: 'account',
      label: 'Mon Compte',
      icon: User,
      subMenus: [
        { id: 'general', label: 'Informations générales' },
        { id: 'security', label: 'Sécurité du compte' },
        { id: 'sessions', label: 'Sessions actives' }
      ]
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      subMenus: [
        { id: 'email', label: 'Notifications par email' },
        { id: 'app', label: 'Notifications dans l\'application' }
      ]
    },
    {
      id: 'data',
      label: 'Projets & Données',
      icon: Database,
      subMenus: [
        { id: 'projects', label: 'Paramètres des projets' },
        { id: 'import-export', label: 'Import/Export' },
        { id: 'rncp-sync', label: 'Synchronisation RNCP' }
      ]
    },
    {
      id: 'team',
      label: 'Équipe & Permissions',
      icon: Users,
      subMenus: [
        { id: 'members', label: 'Membres de l\'équipe' },
        { id: 'roles', label: 'Rôles et permissions' }
      ]
    },
    {
      id: 'security',
      label: 'Sécurité & Confidentialité',
      icon: Lock,
      subMenus: [
        { id: 'activity', label: 'Historique d\'activité' },
        { id: 'privacy', label: 'Données personnelles' }
      ]
    },
    {
      id: 'api',
      label: 'API & Connexions',
      icon: LinkIcon,
      subMenus: [
        { id: 'connections', label: 'Tests de connexion' },
        { id: 'sync', label: 'Synchronisation RNCP-ROME' }
      ]
    },
    {
      id: 'tests',
      label: 'Tests & Développement',
      icon: TestTube,
      subMenus: [
        { id: 'france-travail', label: 'Test France Travail', route: '/france-travail-test' },
        { id: 'rome-competences', label: 'Test ROME Compétences', route: '/rome-competences-test' },
        { id: 'api-general', label: 'Tests API Généraux', route: '/tests-api' },
        { id: 'api-ft-v2', label: 'Test API France Travail v2', route: '/test-api-france-travail' },
        { id: 'scraping', label: 'Test Scraping RNCP', route: '/test-scraping' }
      ]
    },
    {
      id: 'support',
      label: 'Support & Aide',
      icon: HelpCircle,
      subMenus: [
        { id: 'help', label: 'Centre d\'aide' },
        { id: 'contact', label: 'Contact support' },
        { id: 'about', label: 'À propos' }
      ]
    }
  ];

  const [expandedMenus, setExpandedMenus] = useState(['account']);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const renderContent = () => {
    // Mon Compte
    if (activeMenu === 'account' && activeSubMenu === 'general') {
      return <AccountSettings />;
    }
    if (activeMenu === 'account' && activeSubMenu === 'security') {
      return <SecuritySettings />;
    }
    if (activeMenu === 'account' && activeSubMenu === 'sessions') {
      return <SessionsSettings />;
    }

    // Notifications
    if (activeMenu === 'notifications') {
      return <NotificationSettings subMenu={activeSubMenu} />;
    }

    // Projets & Données
    if (activeMenu === 'data') {
      return <DataSettings subMenu={activeSubMenu} />;
    }

    // Équipe & Permissions
    if (activeMenu === 'team' && activeSubMenu === 'members') {
      return <TeamSettings subMenu={activeSubMenu} />;
    }
    if (activeMenu === 'team' && activeSubMenu === 'roles') {
      return <RolesPermissionsSettings />;
    }

    // Sécurité & Confidentialité
    if (activeMenu === 'security') {
      return <SecurityPrivacySettings subMenu={activeSubMenu} />;
    }

    // API & Connexions
    if (activeMenu === 'api') {
      return <ApiConnectionsSettings subMenu={activeSubMenu} />;
    }

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-500">Section en développement...</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <SettingsIcon className="w-8 h-8" />
            Paramètres
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Gérez vos préférences et paramètres de l'application
          </p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Menu latéral */}
          <div className="col-span-12 lg:col-span-3">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <nav className="space-y-1">
                {menuItems.map((menu) => (
                  <div key={menu.id}>
                    <button
                      onClick={() => {
                        toggleMenu(menu.id);
                        setActiveMenu(menu.id);
                        if (menu.subMenus?.length > 0) {
                          setActiveSubMenu(menu.subMenus[0].id);
                        }
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors ${
                        activeMenu === menu.id
                          ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500'
                          : 'text-gray-700 hover:bg-gray-50 border-l-4 border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <menu.icon className="w-5 h-5" />
                        <span>{menu.label}</span>
                      </div>
                      {menu.subMenus && (
                        <svg
                          className={`w-4 h-4 transition-transform ${
                            expandedMenus.includes(menu.id) ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>

                    {/* Sous-menus */}
                    {menu.subMenus && expandedMenus.includes(menu.id) && (
                      <div className="bg-gray-50 border-l-4 border-gray-200">
                        {menu.subMenus.map((subMenu) => (
                          <button
                            key={subMenu.id}
                            onClick={() => {
                              if (subMenu.route) {
                                // Si le sous-menu a une route, naviguer vers cette route
                                navigate(subMenu.route);
                              } else {
                                // Sinon, afficher le contenu dans Settings
                                setActiveMenu(menu.id);
                                setActiveSubMenu(subMenu.id);
                              }
                            }}
                            className={`w-full text-left px-4 py-2 pl-12 text-sm transition-colors ${
                              activeMenu === menu.id && activeSubMenu === subMenu.id
                                ? 'text-primary-700 bg-primary-50 font-medium'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {subMenu.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </div>

          {/* Contenu principal */}
          <div className="col-span-12 lg:col-span-9">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}
